import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Select, MenuItem, TextField, InputAdornment, Checkbox, Typography, Alert, FormHelperText } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import { DayOfWeek, VenueBookedTimeSlot } from '../../../shared/models';
import { TimeRange } from '../../../shared/types';
import { SlotAvailabilitySelector } from './slot_availability_selector';
import { useAvailabilityStore } from '../stores/availability_store';
import { CircularTimeRangeSliderSelector } from './circular_time_range_slider_selector';

interface AvailabilitiesGridTimeSelectorProps {
    possibleTimeRangeForDay: TimeRange[] | null;
    errorMessage: string | null;
    bookedTimeSlotsByDay: Record<string, VenueBookedTimeSlot[]>;
    selectedSlot: { StartTime: Date; EndTime: Date } | null;
    selectedStartTime: Date | null;
    onTimeRangeChange: (startTime: Date, endTime: Date) => void;
    onOverlapErrorChange: (hasError: boolean) => void;
}

const AvailabilitiesGridTimeSelector: React.FC<AvailabilitiesGridTimeSelectorProps> = ({
    possibleTimeRangeForDay,
    errorMessage,
    bookedTimeSlotsByDay,
    selectedSlot,
    selectedStartTime,
    onTimeRangeChange,
    onOverlapErrorChange
}) => {
    const {
        venue,
        selectedSection,
        numberOfPeople,
        openBarSelected,
        setSelectedSection,
        setNumberOfPeople,
        setOpenBarSelected
    } = useAvailabilityStore();
    const timeRangeSelectorRef = useRef<HTMLDivElement>(null);
    const [timesUnavailable, setTimesUnavailable] = useState<VenueBookedTimeSlot[]>([]);
    const bookedSlotsBySectionAndDay = useMemo(() => {
        const slotsBySectionAndDay: Record<string, Record<string, VenueBookedTimeSlot[]>> = {};
        Object.entries(bookedTimeSlotsByDay).forEach(([date, slots]) => {
            slots.forEach(slot => {
                if (!slotsBySectionAndDay[slot.SectionID]) {
                    slotsBySectionAndDay[slot.SectionID] = {};
                }
                if (!slotsBySectionAndDay[slot.SectionID][date]) {
                    slotsBySectionAndDay[slot.SectionID][date] = [];
                }
                slotsBySectionAndDay[slot.SectionID][date].push(slot);
            });
        });
        return slotsBySectionAndDay;
    }, [bookedTimeSlotsByDay]);

    useEffect(() => {
        if (possibleTimeRangeForDay && selectedSection) {
            const dateStr = possibleTimeRangeForDay[0].startTime.toDateString();
            let unavailableTimes = bookedSlotsBySectionAndDay[selectedSection]?.[dateStr] || [];
            if (dateStr === new Date('2024-12-08T00:00:00').toDateString()) {
                console.log('unavailableTimes', unavailableTimes);
            }

            setTimesUnavailable(unavailableTimes);
        }
    }, [possibleTimeRangeForDay, selectedSection, bookedSlotsBySectionAndDay, venue.Sections]);

    const handleSectionChange = (sectionId: string) => {
        setSelectedSection(sectionId);
        const sectionCapacity = venue.Sections[sectionId].Capacity;
        const minPeople = venue.PricingRatesPerSection[sectionId]?.MinimumPeople || 1;

        // Only change number of people if current amount exceeds capacity or is below minimum
        if (numberOfPeople > sectionCapacity) {
            setNumberOfPeople(sectionCapacity);
        } else if (numberOfPeople < minPeople) {
            setNumberOfPeople(minPeople);
        }
    };
    return (
        <div>
            <Box sx={{ marginTop: 2 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
                    <Box sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        gap: 1,
                    }}>
                        <Select
                            value={selectedSection}
                            onChange={(e) => handleSectionChange(e.target.value as string)}
                            size="medium"
                            fullWidth
                            sx={{
                                height: '48px',
                                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                                color: 'white',
                                borderRadius: '8px',
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'rgba(255, 255, 255, 0.2)',
                                    borderWidth: '1px',
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#C0C0DD',
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#C0C0DD',
                                    borderWidth: '2px',
                                },
                                '& .MuiSelect-icon': {
                                    color: 'rgba(255, 255, 255, 0.8)',
                                },
                            }}
                        >
                            {Object.entries(venue.Sections).map(([sectionId, section]) => {
                                const bookedSlots = bookedSlotsBySectionAndDay[sectionId]?.[possibleTimeRangeForDay?.[0].startTime.toDateString()] || [];
                                const isFullyBooked = bookedSlots.some(bookedSlot =>
                                    bookedSlot.StartTime <= possibleTimeRangeForDay[0].startTime &&
                                    bookedSlot.EndTime >= possibleTimeRangeForDay[0].endTime
                                );
                                return (
                                    <MenuItem key={sectionId} value={sectionId} disabled={isFullyBooked}
                                        sx={{
                                            fontSize: '0.95rem',
                                            padding: '12px 16px',
                                            '&:hover': {
                                                backgroundColor: 'rgba(224, 177, 241, 0.08)'
                                            }
                                        }}>
                                        {section.Name} {isFullyBooked && "(Not Available)"}
                                    </MenuItem>
                                );
                            })}
                        </Select>
                        <TextField
                            select
                            label="Number of Guests"
                            value={numberOfPeople}
                            onChange={(e) => {
                                const newValue = Math.max(
                                    venue.PricingRatesPerSection[selectedSection]?.MinimumPeople || 1,
                                    Number(e.target.value)
                                );
                                setNumberOfPeople(newValue);
                            }}
                            variant="outlined"
                            size="medium"
                            sx={{
                                width: { xs: '100%', sm: '180px' },
                                '& .MuiOutlinedInput-root': {
                                    height: '48px',
                                    color: 'white',
                                    backgroundColor: 'rgba(255, 255, 255, 0.08)',
                                    borderRadius: '8px',
                                    '& fieldset': {
                                        borderColor: 'rgba(255, 255, 255, 0.2)',
                                        borderWidth: '1px'
                                    },
                                    '&:hover fieldset': {
                                        borderColor: '#C0C0DD'
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#C0C0DD',
                                        borderWidth: '2px'
                                    },
                                },
                                '& .MuiInputLabel-root': {
                                    color: 'rgba(255, 255, 255, 0.8)',
                                    fontSize: '0.9rem'
                                },
                                '& .MuiSelect-icon': { color: 'rgba(255, 255, 255, 0.8)' },
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <PersonIcon sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '1.2rem' }} />
                                    </InputAdornment>
                                ),
                            }}
                        >
                            {venue.PricingRatesPerSection[selectedSection] && venue.Sections[selectedSection].Capacity && (
                                Array.from({ length: Math.max(0, venue.Sections[selectedSection].Capacity - venue.PricingRatesPerSection[selectedSection].MinimumPeople + 1) }, (_, i) => (
                                    <MenuItem
                                        key={i + venue.PricingRatesPerSection[selectedSection].MinimumPeople}
                                        value={i + venue.PricingRatesPerSection[selectedSection].MinimumPeople}
                                        sx={{
                                            fontSize: '0.95rem',
                                            padding: '12px 16px',
                                            '&:hover': {
                                                backgroundColor: 'rgba(224, 177, 241, 0.08)'
                                            }
                                        }}>
                                        {i + venue.PricingRatesPerSection[selectedSection].MinimumPeople}
                                    </MenuItem>
                                ))
                            )}
                        </TextField>
                    </Box>
                    {venue.PricingRatesPerSection[selectedSection]?.AdditionalCostPerPersonForOpenBar > 0 && (
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            backgroundColor: 'rgba(255, 255, 255, 0.08)',
                            borderRadius: '8px',
                            padding: '8px 12px'
                        }}>
                            <Checkbox
                                checked={openBarSelected}
                                onChange={(e) => setOpenBarSelected(e.target.checked)}
                                size="medium"
                                sx={{
                                    color: 'rgba(255, 255, 255, 0.7)',
                                    '&.Mui-checked': {
                                        color: '#C0C0DD'
                                    },
                                    padding: '8px',
                                }}
                            />
                            <Typography
                                variant="body1"
                                sx={{
                                    color: 'rgba(255, 255, 255, 0.9)',
                                    fontSize: '0.95rem',
                                    fontWeight: 500
                                }}>
                                Add open bar (+${venue.PricingRatesPerSection[selectedSection].AdditionalCostPerPersonForOpenBar}/person)
                            </Typography>
                        </Box>
                    )}
                </Box>

                {possibleTimeRangeForDay && possibleTimeRangeForDay.length > 0 && (
                    <Box
                        ref={timeRangeSelectorRef}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                            justifyContent: 'center',
                            width: '100%'
                        }}
                    >
                        {(() => {
                            const dayOfWeek = possibleTimeRangeForDay[0].startTime.toLocaleString('en-US', { weekday: 'long' }) as DayOfWeek;
                            const dayAvailability = venue.Availability[dayOfWeek];

                            if (dayAvailability?.Slots && dayAvailability.Slots.length > 0) {
                                return (
                                    <SlotAvailabilitySelector
                                        dayAvailability={dayAvailability}
                                        onSlotSelect={(slot) => onTimeRangeChange(slot.StartTime, slot.EndTime)}
                                        selectedSlot={selectedSlot}
                                    />
                                );
                            }

                            const minimumDuration = venue.PricingRatesPerSection[selectedSection]?.MinimumBookingDuration ?? 2;
                            const minimumBlocks = minimumDuration * 2;

                            return (
                                <CircularTimeRangeSliderSelector
                                    timeRanges={possibleTimeRangeForDay.map(range => ({
                                        startTime: range.startTime,
                                        endTime: range.endTime,
                                        rangeId: range.startTime.getTime()
                                    }))}
                                    timesUnavailable={timesUnavailable}
                                    onTimeRangeChange={onTimeRangeChange}
                                    selectedStartTime={selectedStartTime}
                                    onOverlapErrorChange={onOverlapErrorChange}
                                />
                            );
                        })()}
                    </Box>
                )}
            </Box>
            <Box sx={{ mt: 1, mb: 1 }}>
                {errorMessage && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                        {errorMessage}
                    </Alert>
                )}
                {selectedSection && venue.Sections[selectedSection]?.CheckoutPageDescription && (
                    <Alert severity="info" sx={{ mt: 1 }}>
                        {venue.Sections[selectedSection].CheckoutPageDescription}
                    </Alert>
                )}
                {venue.CheckoutPageDescription && (
                    <Alert severity="info" sx={{ mt: 1 }}>
                        {venue.CheckoutPageDescription}
                    </Alert>
                )}
            </Box>
        </div>
    );
};

export default AvailabilitiesGridTimeSelector;
